import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { Transition, UIRouterGlobals } from '@uirouter/core';
import { Subscription } from 'rxjs';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { ModalService } from 'src/app/components/modals/modal.service';
import { DisplayService } from 'src/app/displays/services/display.service';
import { LocalStorageHelperService } from 'src/app/shared/services/local-storage-helper.service';
import { RvshareAppDetectionService } from '../../services/rvshare-app-detection.service';
import { ScreenSharingSessionService } from '../../services/screen-sharing-session.service';
import { ScreenSharingService } from '../../services/screen-sharing.service';
import { MessageType } from '../../types/message-type';
import { ModeratorWebsocketParamsService } from '../../types/moderator-params';

@Component({
  selector: 'moderator-room',
  templateUrl: './moderator-room.component.html',
  styleUrl: './moderator-room.component.scss',
  standalone: false,
})
export class ModeratorRoomComponent implements OnInit, OnDestroy {
  paused = false;
  stopped = true;
  joinedSubscription: Subscription;
  displayName = '';
  shouldLeaveOnDestroy = true;
  private moderatorWebsocketParamsService = inject(
    ModeratorWebsocketParamsService,
  );
  private localStorageHelperService = inject(LocalStorageHelperService);
  additionalDisplayCount = 0;

  get displayId(): string | undefined {
    return this.uiRouterGlobals.params.displayId;
  }

  constructor(
    protected screenSharingService: ScreenSharingService,
    private screenSharingSessionService: ScreenSharingSessionService,
    private displayService: DisplayService,
    private modalService: ModalService,
    private transition: Transition,
    protected rvshareAppDetectionService: RvshareAppDetectionService,
    private uiRouterGlobals: UIRouterGlobals,
    private userStateService: UserStateService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  private async resolveDisplaySelection(
    displayId: string,
    sessionId: string,
    existingSession: boolean,
  ): Promise<{ primaryDisplayId?: string; displayIds: string[] }> {
    const selectedDisplayIds =
      this.screenSharingService.selectedDisplayIds ?? [];
    if (selectedDisplayIds.length > 0) {
      return {
        primaryDisplayId: displayId ?? selectedDisplayIds[0],
        displayIds: selectedDisplayIds,
      };
    }

    if (existingSession) {
      const session =
        await this.screenSharingSessionService.getModeratedSession(sessionId);
      const restoredDisplayIds = session?.displayIds?.length
        ? session.displayIds
        : session?.displayId
          ? [session.displayId]
          : [];

      if (restoredDisplayIds?.length > 0) {
        return {
          primaryDisplayId: session?.displayId ?? restoredDisplayIds[0],
          displayIds: restoredDisplayIds,
        };
      }
    }

    return {
      primaryDisplayId: displayId,
      displayIds: displayId ? [displayId] : [],
    };
  }

  async ngOnInit(): Promise<void> {
    const displayId = this.uiRouterGlobals.params.displayId;
    const sessionId = this.uiRouterGlobals.params.sessionId;
    const existingSessionFlag = this.uiRouterGlobals.params.existingSession;
    const existingSession =
      existingSessionFlag === true || existingSessionFlag === 'true';

    if (!displayId || !sessionId) {
      this.redirect();
      return;
    }

    const { primaryDisplayId, displayIds } = await this.resolveDisplaySelection(
      displayId,
      sessionId,
      existingSession,
    );
    this.screenSharingService.selectedDisplayIds = [...displayIds];
    this.additionalDisplayCount = Math.max(0, displayIds.length - 1);

    const token = this.userStateService.getAccessToken()?.access_token;
    const websocketParams = this.moderatorWebsocketParamsService.create(
      this.userStateService.getUserFullName(),
      primaryDisplayId,
      displayIds,
      token,
      sessionId,
      existingSession,
    );
    this.screenSharingService.join(websocketParams);

    if (primaryDisplayId) {
      this.displayService.getDisplay(primaryDisplayId).then(() => {
        this.displayName = this.displayService.display.name;
      });
    }

    this.transition.router.stateService.go(
      'apps.screen-sharing.moderator-room',
      { existingSession: true },
      { inherit: true },
    );

    this.joinedSubscription = this.screenSharingService.joinedSubject.subscribe(
      (value) => {
        if (value === MessageType.JOINED_CHANNEL) {
          this.stopped = !this.screenSharingService.mediaStream;
          this.localStorageHelperService.set(
            ModeratorWebsocketParamsService.MODERATOR_SESSION_KEY,
            websocketParams.sessionId,
          );
        } else if (value === MessageType.ANSWER) {
          this.stopped = false;
          this.paused = false;
        } else if (value === MessageType.CLIENT_STREAM_CLOSED) {
          this.stopped = true;
          this.paused = false;
          this.changeDetectorRef.detectChanges();
        } else if (value === 'error') {
          this.redirect();
        }
      },
    );
  }

  ngOnDestroy(): void {
    this.joinedSubscription?.unsubscribe();
    if (this.shouldLeaveOnDestroy) {
      this.screenSharingService.leave();
      this.screenSharingService.stopAll();
    }
  }

  redirect() {
    this.transition.router.stateService.go(
      'apps.screen-sharing.moderator-join',
      { reload: true },
    );
  }

  start() {
    this.screenSharingService.start();
    this.paused = false;
    this.stopped = true;
  }

  resume() {
    this.screenSharingService.stream();
    this.paused = false;
    this.stopped = false;
  }

  pause() {
    this.screenSharingService.pause();
    this.paused = true;
    this.stopped = false;
  }

  stop() {
    if (this.screenSharingService.mediaStream) {
      this.screenSharingService.stop();
      this.stopped = true;
    } else {
      this.screenSharingService.stopSharing();
    }
  }

  share(memberId: string) {
    if (memberId === this.screenSharingService.memberId) {
      this.screenSharingService.stopSharing();
      this.start();
    } else {
      this.screenSharingService.invite(memberId);
    }
  }

  remove(id: string) {
    if (id === this.screenSharingService.memberId) {
      this.modalService
        .confirmDanger(
          'Leave room?',
          `Are you sure you want to leave the room? ${
            this.screenSharingService.moderatorCount === 1
              ? 'As you are the only moderator, the room will also be closed after a short delay.'
              : 'As you are not the only moderator, the room will remain open.'
          }`,
          'Leave',
          'Cancel',
        )
        .then(() => {
          this.screenSharingService.leave();
          this.redirect();
        })
        .catch(() => {});
    } else {
      this.modalService
        .confirmDanger(
          'Remove participant?',
          "The participant will be removed from the room's list. They can enter the session code to re-enter the room.",
          'Remove Participant',
          'Cancel',
        )
        .then(() => {
          this.screenSharingService.remove(id);
        })
        .catch(() => {});
    }
  }

  end() {
    this.modalService
      .confirmDanger(
        'End session for all?',
        'All participants, including the moderator, will be removed from the room and the sharing session will end.',
        'End Session for All',
        'Cancel',
      )
      .then(() => {
        this.shouldLeaveOnDestroy = false;
        this.screenSharingService.reset(true);
        this.redirect();
      })
      .catch(() => {});
  }

  allowOthersToJoinChange(checked: boolean) {
    this.screenSharingService.changeAllowOthersToJoin(checked);
  }
}
